import { useState, useEffect } from "react";
import { OptimizedDataDto } from "@reactive-resume/dto";
import { useResumeStore } from "@/client/stores/resume";
import { useGetOptimizationResults } from "@/client/services/resume-optimizer/get-optimization-results";
import {
  useOptimizeResume,
  useDeleteOptimization,
  useAcceptOptimization,
} from "@/client/services/resume-optimizer";

export const useResumeOptimization = () => {
  const resumeId = useResumeStore((state) => state.resume.id);
  const [optimizationData, setOptimizationData] = useState<{ data: OptimizedDataDto } | null>(null);

  // Get optimization results
  const {
    data: fetchedOptimization,
    isLoading: isLoadingOptimization,
    refetch,
    isError,
    error,
  } = useGetOptimizationResults(
    { id: resumeId },
    {
      enabled: Boolean(resumeId),
    },
  );

  // Optimization mutation
  const { optimizeResume, loading: isOptimizing, error: optimizeError } = useOptimizeResume();

  // Delete optimization mutation
  const { deleteOptimization, loading: isDeleting } = useDeleteOptimization();

  // Accept optimization mutation
  const { acceptOptimization, loading: isAccepting } = useAcceptOptimization();

  // Set optimization data when fetched, clear when error (especially 404)
  useEffect(() => {
    if (fetchedOptimization) {
      console.log("📊 [DEBUG] Setting optimization data from fetch:", !!fetchedOptimization.data);
      setOptimizationData({ data: fetchedOptimization.data });
    } else if (isError) {
      console.log("❌ [DEBUG] Query error, clearing optimization data:", error);
      setOptimizationData(null);
    }
  }, [fetchedOptimization, isError, error]);

  // Start optimization process
  const startOptimization = async (answers?: Record<string, string>, focusedMetrics?: string[]) => {
    if (!resumeId) return;

    try {
      await optimizeResume({ id: resumeId, answers, focusedMetrics });
      return true;
    } catch (err) {
      console.error("Error starting optimization:", err);
      return false;
    }
  };

  // Delete optimization process
  const deleteOptimizationData = async () => {
    if (!resumeId) return;

    try {
      await deleteOptimization({ id: resumeId });
      setOptimizationData(null);
      return true;
    } catch (err) {
      console.error("Error deleting optimization:", err);
      return false;
    }
  };

  // Accept optimization process
  const acceptOptimizationData = async () => {
    if (!resumeId) return;

    try {
      await acceptOptimization({ id: resumeId });
      setOptimizationData(null);
      return true;
    } catch (err) {
      console.error("Error accepting optimization:", err);
      return false;
    }
  };

  // Set optimization data externally
  const setOptimization = (data: OptimizedDataDto | null) => {
    console.log("🔄 [DEBUG] Setting optimization data:", data);
    setOptimizationData(data ? { data } : null);
  };

  return {
    optimizationData,
    isLoadingOptimization,
    isOptimizing,
    isDeleting,
    isAccepting,
    startOptimization,
    deleteOptimizationData,
    acceptOptimizationData,
    setOptimization,
    refetch,
    error: optimizeError,
  };
};
